import dayjs from "dayjs";
import type { ReactNode } from "react";
import { Config } from "@/app/config";
import type {
	AbstractLeadFormProps,
	AbstractLeadFormState,
} from "@/component/assets/AbstractLeadForm/AbstractLeadForm";
import { AbstractLeadForm } from "@/component/assets/AbstractLeadForm/AbstractLeadForm";
import type { AbstractLeadFormFields, LeadRequest, ProductType } from "@/component/assets/AbstractLeadForm/types";
import type { ProductRequisitionTime } from "@/component/Mortgage/LeadForm/types";
import type { EventComponentType } from "@/shared/lib/google-analytics-4.type";
import { logError } from "@/shared/lib/sentry-error";
import type { FormIncomeSimpleData } from "@/shared/ui";
import { http } from "@/shared/ui";
import { getOtherData } from "../Calculator/domain";
import type { PersonalLoanSearchParams } from "../Calculator/types";
import * as Step from "./Steps";
import type { PersonalLoanLeadCalcRequest, PersonalLoanLeadCalcResponse } from "./types";
import "./style.scss";

export interface PersonalLoanLeadFormFields extends AbstractLeadFormFields {
	loanAmount: number;
	incomeList?: FormIncomeSimpleData[];
	inKHR: boolean;
	wantsWithOnlineService: boolean;
	productRequisitionTime: ProductRequisitionTime;
	isCash: boolean;
}

export interface PersonalLoanLeadFormProps<SearchParams = PersonalLoanSearchParams>
	extends AbstractLeadFormProps<PersonalLoanLeadCalcRequest, PersonalLoanLeadCalcResponse, SearchParams> {
	variant?: string;
	showDetails?: boolean;
	withOnlineOption?: boolean;
	isInListingCalculator?: boolean;
	withRequisitionTimeOption?: boolean;
}

export interface PersonalLoanLeadFormState<FormFields extends PersonalLoanLeadFormFields = PersonalLoanLeadFormFields>
	extends AbstractLeadFormState<FormFields> {}

/**
 * @Shortcode
 *
 * @title "Személyi kölcsön leadform"
 * @shortcode "personal_loan_lead"
 * @slug "szemelyi-kolcson"
 * @type "leadform"
 */
export class PersonalLoanLeadForm<
	TSearchParams = PersonalLoanSearchParams,
	State extends PersonalLoanLeadFormState = PersonalLoanLeadFormState,
> extends AbstractLeadForm<
	PersonalLoanLeadCalcRequest,
	PersonalLoanLeadCalcResponse,
	PersonalLoanLeadFormFields,
	PersonalLoanLeadFormProps<TSearchParams>,
	State
> {
	// EventParams
	protected readonly productTypes: string = `${
		this.props.variant === "step-by-step" ? "PERSONAL_LOAN-MENNYI_HITELT_KAPHATOK" : "PERSONAL_LOAN"
	}`;

	protected readonly componentType: EventComponentType = "leadform";

	public static defaultProps = {
		title: "Kérj ingyenes szakértői segítséget!",
	};

	protected renderSteps(): ReactNode {
		const {
			variant = "",
			title,
			showDetails,
			withOnlineOption,
			withRequisitionTimeOption,
			isInListingCalculator,
		} = this.props;
		const {
			submitState,
			values: { email },
			leadId,
		} = this.state;

		const request = this.getCalculatorRequest();
		const response = this.getCalculatorResponse();

		return (
			<>
				<Step.LeadForm
					stepName="lead-form"
					submitState={submitState}
					defaultTarget={isInListingCalculator ? "success-lead" : "lead-income"}
					title={title}
					loanAmount={request?.loanAmount}
					maturityYears={request?.maturityYears}
					installment={response?.startInstallment}
					apr={response?.apr}
					showDetails={showDetails}
					withInKhr={variant !== "step-by-step"}
					withOnlineOption={withOnlineOption}
					withRequisitionTimeOption={withRequisitionTimeOption}
					variant={variant}
					crossSellOption
				/>

				{variant !== "step-by-step" && <Step.Khr stepName="khr" />}

				<Step.IncomePage
					stepName="lead-income"
					defaultTarget="success-lead"
					className="bg-success text-white"
					submitState={submitState}
					loanAmount={request?.loanAmount}
					incomeList={request?.incomeList}
				/>
				<Step.SuccessPage
					stepName="success-lead"
					leadId={leadId}
					userEmail={email}
					ga4SendEventParameters={{
						providerName: response?.bankName,
						productName: response?.productName,
						productId: response?.id,
					}}
				/>
			</>
		);
	}

	protected onStep(target: string, withEvent: boolean): void {
		const { isInListingCalculator } = this.props;
		const { leadId } = this.state;

		if (target === "success-lead" && leadId) {
			const { request } = this.props;
			const {
				values: { loanAmount, incomeList = request?.incomeList || [], email },
			} = this.state;

			this.setState({
				submitState: "loading",
			});

			const otherData = getOtherData({
				...this.props.searchParams,
				...this.state.values,
				incomeList,
			});

			this.promisePool
				.add(
					http.put(
						Config.getWpRestApiUrl(`/myfinances/lead-and-customer/${encodeURIComponent(leadId)}`, {
							email: email,
						}),
						{
							leadData: {
								loan_amount: loanAmount,
								other_data_json: otherData.getObject(),
							},
						},
					),
				)
				.then(() => {
					this.setState({
						submitState: "done",
					});

					super.onStep(target, true);
				})
				.catch((error: Error) => {
					this.setState({
						submitState: "error",
					});

					logError(error);
				})
				.finally(() => {
					this.promisePool.addTimeout().then(() => {
						this.setState({
							submitState: undefined,
						});
					});
				});
		} else if (leadId === undefined && (target === "lead-income" || target === "success-lead")) {
			const { variant } = this.props;
			const {
				values: { inKHR },
			} = this.state;

			if (inKHR && variant !== "step-by-step") {
				super.onStep("khr", withEvent);
			} else if (target === "success-lead" || (!isInListingCalculator && target === "lead-income")) {
				this.sendLead()
					.then((response) => {
						this.setState({
							submitState: undefined,
							leadId: response.leadId,
						});

						super.onStep(target, false);
					})
					.catch((error: Error) => {
						// TODO: Hibakezelés
						logError(error);
					});
			}
		} else {
			super.onStep(target, withEvent);
		}
	}

	protected getLeadData(): LeadRequest<PersonalLoanLeadCalcRequest, PersonalLoanLeadCalcResponse> {
		const { request, variant, searchParams } = this.props;
		const {
			values: { inKHR = request.inKHR, wantsWithOnlineService, isCash },
			values,
		} = this.state;

		const data = {
			...super.getLeadData(),
			inKHR,
			wantsWithOnlineService,
			isCash,
		};

		if (variant === "step-by-step") {
			data.productSubType = "MENNYI_HITELT_KAPHATOK";
		}

		const otherData = getOtherData({
			...searchParams,
			incomeList: request?.incomeList || [],
			...values,
		});

		data.request.otherDataJson = otherData.getJson();

		return data;
	}

	protected getLeadDataRequest(): PersonalLoanLeadCalcRequest {
		const { request } = this.props;
		const {
			values: {
				inKHR = request?.inKHR,
				loanAmount = request?.loanAmount,
				incomeList = request?.incomeList || [],
				productRequisitionTime,
			},
		} = this.state;

		return { ...request, inKHR, loanAmount, incomeList, productRequisitionTime };
	}

	protected getAutoresponderName(): string {
		return "autoresponder-personal-loan";
	}

	protected getAutoresponderFields(): Record<string, any> {
		const request = this.getLeadDataRequest();
		const response = this.getLeadDataResponse();
		const {
			values: { firstName, lastName, email, inKHR, loanAmount, incomeList = request?.incomeList || [] },
		} = this.state;

		const threeMonthTypes = ["SALARY-H", "SALARY-X", "ENT-H"];
		const wholeYearTypes = ["KATA", "EVA", "DIVIDEND", "ESTATE"];
		const onProbation =
			incomeList.length !== 0 &&
			!incomeList.find((inc) => {
				if (inc.incomeStart) {
					if (threeMonthTypes.includes(inc.incomeType)) {
						return dayjs().diff(dayjs(inc.incomeStart), "days") >= 91;
					}

					if (wholeYearTypes.includes(inc.incomeType)) {
						return dayjs().diff(dayjs(inc.incomeStart), "days") >= 366;
					}
				}

				return true;
			});

		return {
			firstName,
			lastName,
			email,
			inKhr: inKHR,
			loanAmount: loanAmount || request?.loanAmount,
			productName: response?.productName,
			onProbation,
		};
	}

	protected getProductType(): ProductType {
		return "PERSONAL_LOAN";
	}

	protected monthDiff(d1: string): number {
		const months = dayjs().diff(dayjs(d1), "month");

		return months <= 0 ? 0 : months;
	}

	protected getComponentName(): string {
		return "PersonalLoanLeadForm";
	}
}

export default PersonalLoanLeadForm;
